import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import type { DatesSetArg, EventClickArg, EventContentArg, EventMountArg } from '@fullcalendar/core';
import type { CalendarMeetingEvent } from '@app-types';
import { getMeetingStatusLabel } from '@utils/referenceCatalog';
import EmptyState from '@components/common/EmptyState';
import styles from './MeetingsCalendar.module.css';

interface MeetingsCalendarProps {
  events: CalendarMeetingEvent[];
  onDatesChange: (start: string, end: string) => void;
  isLoading?: boolean;
}

const STATUS_COLORS: Record<string, { backgroundColor: string; borderColor: string }> = {
  scheduled: { backgroundColor: 'var(--color-primary-100)', borderColor: 'var(--color-primary-500)' },
  rescheduled: { backgroundColor: 'var(--color-warning-100)', borderColor: 'var(--color-warning-500)' },
  completed: { backgroundColor: 'var(--color-success-100)', borderColor: 'var(--color-success-500)' },
  cancelled: { backgroundColor: 'var(--color-neutral-100)', borderColor: 'var(--color-neutral-400)' },
};

interface CalendarEventContentProps {
  arg: EventContentArg;
}

/** Renders a single calendar event's content. Hoisted to module scope (rather than
 * defined inline as FullCalendar's `eventContent` callback) so it isn't recreated —
 * and its DOM remounted — on every MeetingsCalendar render. */
function CalendarEventContent({ arg }: CalendarEventContentProps) {
  const meetingEvent = arg.event.extendedProps.meetingEvent as CalendarMeetingEvent;
  return (
    <div className={styles.eventContent}>
      <div className={styles.eventTitle}>{arg.event.title}</div>
      {meetingEvent.assessor && <div className={styles.eventAssessor}>{meetingEvent.assessor.name}</div>}
      {!meetingEvent.is_grouped && meetingEvent.contracts[0] && (
        <>
          <div className={styles.eventSupplier}>{meetingEvent.contracts[0].supplier_name}</div>
          {meetingEvent.contracts[0].tier && <div className={styles.eventTier}>{meetingEvent.contracts[0].tier}</div>}
        </>
      )}
    </div>
  );
}

/** FullCalendar's `eventContent` prop, as a stable module-level reference (not
 * recreated per render) so it never reads as an inline component definition. */
const renderEventContent = (arg: EventContentArg) => <CalendarEventContent arg={arg} />;

function buildTooltipText(event: CalendarMeetingEvent): string {
  const lines: string[] = [];
  lines.push(`Status: ${getMeetingStatusLabel(event.status)}`);

  if (event.assessor) {
    lines.push(`Assessor: ${event.assessor.name}`);
  }

  if (event.is_grouped) {
    lines.push(`Contracts (${event.contract_count}):`);
    for (const c of event.contracts) {
      const parts = [c.contract_number, c.supplier_name, c.tier].filter(Boolean);
      lines.push(`  ${parts.join(' · ')}`);
    }
  } else if (event.contracts[0]) {
    const c = event.contracts[0];
    lines.push(`Contract: ${c.contract_number}`);
    if (c.supplier_name) lines.push(`Supplier: ${c.supplier_name}`);
    if (c.tier) lines.push(`Tier: ${c.tier}`);
    if (c.division) lines.push(`Division: ${c.division}`);
  }

  return lines.join('\n');
}

export function MeetingsCalendar({ events, onDatesChange, isLoading }: MeetingsCalendarProps) {
  const navigate = useNavigate();
  const showEmpty = !isLoading && events.length === 0;

  const calendarEvents = useMemo(() => {
    return events.map((event) => {
      const title = event.is_grouped
        ? `${event.contract_count} contracts`
        : event.contracts[0]?.contract_number || 'Meeting';

      const colors = STATUS_COLORS[event.status] || STATUS_COLORS.scheduled;
      const isCancelled = event.status === 'cancelled';

      return {
        id: String(event.id),
        title,
        start: event.scheduled_datetime,
        extendedProps: {
          meetingEvent: event,
        },
        ...colors,
        textColor: 'var(--color-text-primary)',
        classNames: isCancelled ? [styles.cancelledEvent] : [],
      };
    });
  }, [events]);

  const handleEventClick = useCallback(
    (info: EventClickArg) => {
      const meetingId = info.event.id;
      navigate(`/contract-assessment-meetings/${meetingId}`);
    },
    [navigate]
  );

  const handleDatesSet = useCallback(
    (arg: DatesSetArg) => {
      const start = arg.startStr.split('T')[0];
      const end = arg.endStr.split('T')[0];
      onDatesChange(start, end);
    },
    [onDatesChange]
  );

  const handleEventDidMount = useCallback((info: EventMountArg) => {
    const meetingEvent = info.event.extendedProps.meetingEvent as CalendarMeetingEvent;
    info.el.setAttribute('title', buildTooltipText(meetingEvent));
  }, []);

  return (
    <div className={styles.calendarWrapper}>
      {isLoading && <div className={styles.loadingOverlay}>Loading...</div>}
      {showEmpty && (
        <div className={styles.emptyOverlay}>
          <EmptyState type="meetings" />
        </div>
      )}
      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'timeGridWeek,dayGridMonth',
        }}
        events={calendarEvents}
        eventClick={handleEventClick}
        datesSet={handleDatesSet}
        eventDidMount={handleEventDidMount}
        nowIndicator={true}
        allDaySlot={false}
        slotMinTime="00:00:00"
        slotMaxTime="24:00:00"
        weekends={false}
        height="auto"
        eventDisplay="block"
        eventTimeFormat={{
          hour: '2-digit',
          minute: '2-digit',
          meridiem: false,
          hour12: false,
        }}
        eventContent={renderEventContent}
      />
    </div>
  );
}
